import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RiskBadge from "@/components/RiskBadge";
import { prettyType } from "@/lib/intel";
import { Search as SearchIcon, FileText, Boxes, FolderKanban, EyeOff } from "lucide-react";

const TYPE_TABS = [
  { key: "", label: "All" },
  { key: "ads", label: "Advertisements" },
  { key: "entities", label: "Entities" },
  { key: "cases", label: "Cases" },
];

const LANG_TABS = [
  { key: "", label: "All languages" },
  { key: "en", label: "English" },
  { key: "kn", label: "Kannada" },
  { key: "mixed", label: "Mixed" },
];

export function Highlight({ text, terms }) {
  if (!text) return null;
  if (!terms?.length) return <>{text}</>;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
  if (!escaped.length) return <>{text}</>;
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  return (
    <>
      {text.split(re).map((part, i) =>
        re.test(part) ? <mark key={`${part}-${i}`} className="bg-amber-200/70 rounded-sm px-0.5">{part}</mark> : part
      )}
    </>
  );
}

const MODE_HINT = {
  phone: "Phone-number search — matches full numbers and masked/obfuscated patterns.",
  username: "Handle search — matches usernames and leet/spelling-variant aliases.",
  partial: "No exact word matches — showing partial matches instead.",
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [tab, setTab] = useState("");
  const [lang, setLang] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (val, types, langKey) => {
    if (!val?.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/search", {
        params: { q: val, types: types || undefined, lang: langKey || undefined, limit: 20 },
      });
      setResult(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const initial = params.get("q");
    if (initial) { setQ(initial); doSearch(initial, tab, lang); }
  }, [params, doSearch, tab, lang]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ q });
    doSearch(q, tab, lang);
  };

  const switchTab = (key) => {
    setTab(key);
    if (q.trim()) doSearch(q, key, lang);
  };

  const switchLang = (key) => {
    setLang(key);
    if (q.trim()) doSearch(q, tab, key);
  };

  const terms = result?.terms || [];
  const total = (result?.ads?.length || 0) + (result?.entities?.length || 0) + (result?.cases?.length || 0);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-4xl" data-testid="search-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-blue-700">Full-text search</div>
        <h1 className="font-display font-bold tracking-tight text-3xl">Global search</h1>
        <p className="text-slate-500 text-sm mt-2">
          Relevance-ranked search across advertisements, entities and cases. Phone fragments,
          masked numbers and username variants are resolved automatically.
        </p>
      </header>

      <form onSubmit={onSubmit} className="flex gap-3 border border-slate-200 bg-white rounded-md p-3">
        <div className="relative flex-1">
          <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder='e.g. "massage koramangala", 8450112, @priya.blr' className="pl-9" />
        </div>
        <Button type="submit" data-testid="global-search-submit" className="bg-slate-900 hover:bg-slate-800">
          Search
        </Button>
      </form>

      <div className="flex gap-1.5" data-testid="search-type-tabs">
        {TYPE_TABS.map((t) => (
          <button key={t.key} type="button" data-testid={`search-tab-${t.key || "all"}`}
            onClick={() => switchTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {(tab === "" || tab === "ads") && (
        <div className="flex gap-1.5 items-center" data-testid="search-lang-tabs">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Language</span>
          {LANG_TABS.map((l) => (
            <button
              key={l.key || "all"}
              type="button"
              data-testid={`search-lang-${l.key || "all"}`}
              onClick={() => switchLang(l.key)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                lang === l.key
                  ? "bg-amber-500 text-white border-amber-500"
                  : "border-slate-300 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500" data-testid="search-loading">Searching…</div>}

      {result && !loading && (
        <div className="space-y-6">
          <div className="text-xs text-slate-500 font-mono" data-testid="search-summary">
            {total} result{total === 1 ? "" : "s"} for “{result.query}”
            {MODE_HINT[result.mode] && <span className="ml-2 text-amber-700">— {MODE_HINT[result.mode]}</span>}
          </div>

          {result.ads?.length > 0 && (
            <section data-testid="search-ads-section">
              <h2 className="font-display font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" /> Advertisements ({result.ads.length})
              </h2>
              <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
                {result.ads.map((a) => (
                  <Link key={a.id} to={`/ads/${a.id}`} data-testid={`search-ad-${a.id}`}
                    className="block px-4 py-3 border-b border-slate-100 hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate"><Highlight text={a.title} terms={terms} /></div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          <Highlight text={a.snippet || ""} terms={terms} />
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {a.matched_via && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1">
                              {a.matched_via.includes("masked") || a.matched_via.includes("alias") ? <EyeOff className="h-3 w-3" /> : null}
                              via {a.matched_via}
                            </span>
                          )}
                          {a.lang && a.lang !== "en" && a.lang !== "unknown" && (
                            <span
                              data-testid={`search-ad-lang-${a.id}`}
                              className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                                a.lang === "kn"
                                  ? "bg-amber-50 border-amber-200 text-amber-800"
                                  : "bg-purple-50 border-purple-200 text-purple-800"
                              }`}
                              title={`Ad language: ${a.lang}`}
                            >
                              {a.lang === "kn" ? "KN" : "KN+EN"}
                            </span>
                          )}
                          {a.locality && <span className="text-[10px] font-mono text-slate-400">{a.locality}</span>}
                        </div>
                      </div>
                      {a.risk && <RiskBadge level={a.risk.level} score={a.risk.score} />}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {result.entities?.length > 0 && (
            <section data-testid="search-entities-section">
              <h2 className="font-display font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
                <Boxes className="h-4 w-4 text-purple-600" /> Entities ({result.entities.length})
              </h2>
              <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
                {result.entities.map((e) => (
                  <Link key={e.id} to={`/intel/entities/${e.id}`} data-testid={`search-entity-${e.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50">
                    <div className="text-sm truncate"><Highlight text={(e.label || "").replace("[DEMO] ", "")} terms={terms} /></div>
                    <div className="flex items-center gap-2 shrink-0">
                      {e.network && <span className="text-[10px] font-mono text-slate-400">{e.network}</span>}
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{prettyType(e.type)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {result.cases?.length > 0 && (
            <section data-testid="search-cases-section">
              <h2 className="font-display font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
                <FolderKanban className="h-4 w-4 text-teal-600" /> Cases ({result.cases.length})
              </h2>
              <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
                {result.cases.map((c) => (
                  <Link key={c.id} to={`/cases/${c.id}`} data-testid={`search-case-${c.id}`}
                    className="block px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium truncate"><Highlight text={c.title} terms={terms} /></div>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{c.status}</span>
                    </div>
                    {c.snippet && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1"><Highlight text={c.snippet} terms={terms} /></div>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {total === 0 && (
            <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" data-testid="search-no-results">
              No results for “{result.query}”. Try fewer words, a phone fragment, or an @handle.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
